// 各プロジェクトの未完アクション（未解決 issue）件数をリアルタイム集計するフック（S-061-6）。
//
// 建築レンズのプロジェクト一覧に「未完アクション件数バッジ」を出すための件数源。
//   - enabled=false のときは購読を一切張らない。営業・未設定ユーザー、および無料プランには
//     read を一切発生させない（"営業・未設定ユーザー完全無風" ＋ S-061-5 有料ゲートの継承）。
//   - 件数のみ（スコアなし＝DEC-009）。未解決判定は focus.isUnresolved（canonical）を流用する。
//   - meetings は読まない（decisions 不要＝meetings 読取増ゼロ／設計doc §3-2）。
import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { isUnresolved } from './focus';

export function useUnresolvedCounts(
  projectIds: string[],
  enabled: boolean,
): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const subsRef = useRef<Record<string, () => void>>({});

  // 購読集合の安定キー（順序非依存）。enabled=false なら空キーで購読を畳む。
  const key = enabled ? [...projectIds].sort().join('|') : '';

  useEffect(() => {
    const subs = subsRef.current;

    if (!enabled) {
      Object.values(subs).forEach((u) => u());
      subsRef.current = {};
      setCounts({});
      return;
    }

    // 新規プロジェクト分を購読。
    projectIds.forEach((pid) => {
      if (subs[pid]) return;
      subs[pid] = onSnapshot(
        collection(db, 'projects', pid, 'issues'),
        (snap) => {
          const n = snap.docs.reduce(
            (acc, d) => acc + (isUnresolved(d.data() as any) ? 1 : 0),
            0,
          );
          setCounts((prev) => ({ ...prev, [pid]: n }));
        },
        (e: any) => {
          console.error('[useUnresolvedCounts] failed to subscribe issues:', pid, e);
        },
      );
    });

    // 抜けたプロジェクト分を解除＆破棄。
    Object.keys(subs).forEach((pid) => {
      if (!projectIds.includes(pid)) {
        subs[pid]();
        delete subs[pid];
        setCounts((prev) => {
          const c = { ...prev };
          delete c[pid];
          return c;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // アンマウントで全解除。
  useEffect(
    () => () => {
      Object.values(subsRef.current).forEach((u) => u());
      subsRef.current = {};
    },
    [],
  );

  return counts;
}
